/**
 * DEFCAT P6 — FE write-gating for the Defect Catalogue editor.
 *
 * Mirrors the defect-catalogue role matrix (`.vibe/_DEFECTS/10_CONCEPT.md`
 * §"Role matrix" + `12_ADMIN_WRITE_API.md` §0):
 *
 * | Action                                              | tenant scope                  | platform scope |
 * |-----------------------------------------------------|-------------------------------|----------------|
 * | Create / edit a draft (entries · validity ·         | tenant_admin + expert_analyst | platform_admin |
 * |   axis-levels · ai-map · metadata · branch-a-draft) |                               |                |
 * | Publish a version (live, immutable)                 | tenant_admin                  | platform_admin |
 *
 * READ stays open to any authenticated user (the route carries no gate); only
 * the write controls below are hidden.
 *
 * The backend is the security boundary — the taxonomy admin service re-checks
 * `hasRole(...)` (403) and the B1 invariant 404s any write to a
 * shared-but-not-owned catalogue. This hook is a UX nicety so a user without
 * the role never sees a control the server will refuse on save.
 *
 * Role IDENTITY (not a role-matrix permission key) is the right lever: the BE
 * gates these writes via `hasRole(claims, 'tenant_admin' | 'platform_admin')`,
 * NOT through the `GET /role-matrix` permission grid that `useCanEditResource`
 * consumes. So we use the sanctioned `useHasRole` escape hatch documented in
 * `packages/auth/src/useCurrentUserRole.ts`.
 */
import { useHasRole } from "@aeriosiq/auth-client";
import type { CatalogueScope } from "@aeriosiq/types";

export interface CatalogueWritePermissions {
  /**
   * Edit a draft: entry CRUD, validity overlay, axis-levels, ai-map, catalogue
   * metadata, branch-a-draft — and (on the list page) create a catalogue.
   */
  canEditDraft: boolean;
  /** Publish a draft → live immutable version. */
  canPublish: boolean;
  /** Create a PLATFORM-scoped catalogue (else only tenant scope is offered). */
  canCreatePlatform: boolean;
}

/**
 * @param scope the catalogue's stored scope, or `undefined` on the list/create
 *   surface (treated as tenant, the common case in the tenant app).
 */
export function useCatalogueWritePermissions(
  scope: CatalogueScope | undefined,
): CatalogueWritePermissions {
  const isTenantAdmin = useHasRole("tenant_admin");
  const isExpertAnalyst = useHasRole("expert_analyst");
  const isPlatformAdmin = useHasRole("platform_admin");

  const isPlatform = scope === "platform";
  return {
    canEditDraft: isPlatform
      ? isPlatformAdmin
      : isTenantAdmin || isExpertAnalyst,
    canPublish: isPlatform ? isPlatformAdmin : isTenantAdmin,
    canCreatePlatform: isPlatformAdmin,
  };
}
